// Sales dashboard follow-ups (Stephanie 2026-10-14):
//   • Production needs you — POs production marked F/U Rep on the rep's
//     projects, with production's note.
//   • Waiting on clients — the rep's own "waiting on client" follow-ups (set on
//     the project Overview), soonest date first, with Done.
// Each section hides when empty.
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EditDeleteNote } from "@/components/notes/EditDeleteNote";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/utils";

interface NeedRow {
  poId: string;
  poNumber: number;
  vendorName: string;
  clientName: string;
  projectId: string;
  projectName: string;
  note: string | null;
  notedBy: string | null;
  notedAt: string | null;
}

export interface FollowUpRow {
  id: string;
  projectId: string;
  projectName: string;
  clientName: string;
  reason: string;
  followUpOn: string;
  createdBy: string | null;
  createdAt: string;
}

const btn =
  "inline-flex h-9 items-center rounded-md border border-rule bg-white px-3 text-sm font-medium text-ink hover:bg-canvas disabled:opacity-50";
const card = "rounded-md border border-rule bg-white p-[22px]";
const row = "flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-[#e8e8e5] py-3.5 first:pt-0 last:border-0 last:pb-0";

const todayYmd = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const shortDate = (ymd: string): string =>
  new Date(`${ymd}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export function ProductionNeedsYou() {
  const q = useQuery({
    queryKey: ["sales", "production-needs"],
    queryFn: () => api<{ pos: NeedRow[] }>("/sales/production-needs"),
  });
  const pos = q.data?.pos ?? [];
  if (q.isLoading || q.isError || pos.length === 0) return null;
  return (
    <section className={card} aria-labelledby="production-needs">
      <div className="mb-3.5 flex items-baseline justify-between gap-3">
        <h2 id="production-needs" className="text-lg font-semibold tracking-[-0.4px] text-ink">Production needs you.</h2>
        <span className="text-xs text-muted">{pos.length} {pos.length === 1 ? "PO" : "POs"} marked F/U Rep</span>
      </div>
      <ul>
        {pos.map((p) => (
          <li key={p.poId} className={row}>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-ink">
                {p.clientName} · PO #{p.poNumber} · {p.vendorName}
              </p>
              {p.note ? (
                <p className="mt-1.5 whitespace-pre-wrap rounded-md bg-cream-50 px-3 py-2 text-sm text-ink">
                  {p.note}
                  <span className="mt-1 block text-xs text-muted">
                    {p.notedBy ?? "Production"}
                    {p.notedAt ? ` · ${timeAgo(p.notedAt)}` : ""}
                  </span>
                </p>
              ) : (
                <p className="mt-1 text-xs text-muted">{p.projectName} · no note — check with production</p>
              )}
            </div>
            <Link to={`/projects/${p.projectId}/pos/${p.poId}`} className={btn}>
              Open PO #{p.poNumber}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function WaitingOnClients() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["sales", "follow-ups"],
    queryFn: () => api<{ available: boolean; follow_ups: FollowUpRow[] }>("/sales/follow-ups"),
  });
  const done = useMutation({
    mutationFn: (id: string) => api(`/follow-ups/${id}/done`, { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["sales", "follow-ups"] }),
  });
  const rows = q.data?.follow_ups ?? [];
  if (q.isLoading || q.isError || rows.length === 0) return null;
  const today = todayYmd();
  return (
    <section className={card} aria-labelledby="waiting-clients">
      <div className="mb-3.5 flex items-baseline justify-between gap-3">
        <h2 id="waiting-clients" className="text-lg font-semibold tracking-[-0.4px] text-ink">Waiting on clients.</h2>
        <span className="text-xs text-muted">{rows.length} {rows.length === 1 ? "follow-up" : "follow-ups"}</span>
      </div>
      <ul>
        {rows.map((f) => {
          const due = f.followUpOn <= today;
          return (
            <li key={f.id} className={row}>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">
                  {f.clientName} · {f.projectName}
                </p>
                <EditDeleteNote
                  noun="follow-up"
                  fields={[
                    { key: "reason", label: "Reason", value: f.reason },
                    { key: "date", label: "Follow up on", value: f.followUpOn, kind: "date" },
                  ]}
                  onSave={async (v) => {
                    await api(`/follow-ups/${f.id}`, { method: "PATCH", body: JSON.stringify({ reason: (v.reason ?? "").trim(), follow_up_on: v.date }) });
                    await qc.invalidateQueries({ queryKey: ["sales", "follow-ups"] });
                  }}
                  onDelete={async () => {
                    await api(`/follow-ups/${f.id}`, { method: "DELETE" });
                    await qc.invalidateQueries({ queryKey: ["sales", "follow-ups"] });
                  }}
                >
                  <p className="mt-1 text-sm text-ink">{f.reason}</p>
                  <p className={`mt-1 text-xs ${due ? "font-semibold text-warn" : "text-muted"}`}>
                    {f.followUpOn < today ? "Follow up was due " : due ? "Follow up today, " : "Follow up "}
                    {shortDate(f.followUpOn)}
                  </p>
                </EditDeleteNote>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" className={btn} disabled={done.isPending} onClick={() => done.mutate(f.id)}>
                  Done
                </button>
                <Link to={`/projects/${f.projectId}`} className={btn}>
                  Open project
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
